import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import lodash from "lodash";
import { v4 as uuidv4 } from "uuid";
import { AnalystProto, DataScopeProto } from "build-ts/analyst";
import { MetadataTypeEnum } from "build-ts/enums";
import { getAnalyst, createAnalyst, updateAnalyst } from "services/AnalystService";
import { updateHeader } from "services/MetadataServices";
import showToast from "hooks/useCustomToast";
import AnalystDetailViewComponent from "modules/analyst/components/AnalystDetailView";

interface AnalystDetailViewProps {
  isCreatingNew: boolean;
  onBack: () => void;
}

const AnalystDetailView: React.FC<AnalystDetailViewProps> = ({ isCreatingNew, onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Both analyst and job IDs come from the URL — no need to thread them as props
  const { id: analystId, jobId: routeJobId } = useParams<{ id?: string; jobId?: string }>();

  const [selectedAnalystData, setSelectedAnalystData] = useState<AnalystProto | undefined>(undefined);
  const [originalAnalystData, setOriginalAnalystData] = useState<AnalystProto | undefined>(undefined);
  const [isLoadingAnalystDetails, setIsLoadingAnalystDetails] = useState<boolean>(!!analystId);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [hasDiscardedChanges, setHasDiscardedChanges] = useState<boolean>(false);
  const [isViewingOrEditingJob, setIsViewingOrEditingJob] = useState<boolean>(false);
  const [attributesNeedRefresh, setAttributesNeedRefresh] = useState<boolean>(false);
  const [isScopeLocked, setIsScopeLocked] = useState<boolean>(false);
  const refreshAttributeValuesRef = useRef<(() => Promise<void>) | null>(null);
  const [analystSavedAt, setAnalystSavedAt] = useState<number>(0);
  const [isEditingAnalystName, setIsEditingAnalystName] = useState(false);
  const [analystNameDraft, setAnalystNameDraft] = useState("");

  const originalDataRef = useRef<{
    name: string;
    agentInstructions: string;
    notes: string;
    dsUseDefaultScope: boolean;
    dsMetrics: string[];
    dsBenchmarks: string[];
    dsColumnScopes: Array<{
      id: string | undefined;
      operator: number | undefined;
      values: string[] | undefined;
      is_hidden: boolean | undefined;
      available_values: string[] | undefined;
    }>;
  }>({
    name: "",
    agentInstructions: "",
    notes: "",
    dsUseDefaultScope: true,
    dsMetrics: [],
    dsBenchmarks: [],
    dsColumnScopes: [],
  });

  const syncOriginalData = (data: AnalystProto | undefined) => {
    originalDataRef.current = {
      name: data?.header?.name || "",
      agentInstructions: data?.instructions || "",
      notes: data?.notes || "",
      dsUseDefaultScope: data?.data_scope ? data.data_scope.use_default_scope || false : true,
      dsMetrics: (data?.data_scope?.metrics || [])
        .map(m => m.header?.id)
        .filter((id): id is string => !!id)
        .sort(),
      dsBenchmarks: (data?.data_scope?.benchmarks || [])
        .map(b => b.header?.id)
        .filter((id): id is string => !!id)
        .sort(),
      dsColumnScopes: (data?.data_scope?.column_scopes || []).map(cs => ({
        id: cs?.kg_column?.header?.id,
        operator: cs.operator,
        values: cs.values ? [...cs.values].sort() : undefined,
        is_hidden: cs.is_hidden,
        available_values: cs.available_values ? [...cs.available_values].sort() : undefined,
      })),
    };
  };

  const resetOriginalData = () => {
    originalDataRef.current = {
      name: "",
      agentInstructions: "",
      notes: "",
      dsUseDefaultScope: true,
      dsMetrics: [],
      dsBenchmarks: [],
      dsColumnScopes: [],
    };
  };

  // Derive active tab index from URL
  const getActiveTab = isCreatingNew
    ? 0
    : location.pathname.includes("/memory")
      ? 1
      : location.pathname.includes("/jobs")
        ? 2
        : 0;

  // Fetch analyst on analystId change
  useEffect(() => {
    if (!analystId || isCreatingNew) {
      setSelectedAnalystData(undefined);
      setOriginalAnalystData(undefined);
      resetOriginalData();
      return;
    }

    const load = async () => {
      setIsLoadingAnalystDetails(true);
      const data = await getAnalyst(analystId);
      setIsLoadingAnalystDetails(false);

      if (data) {
        setSelectedAnalystData(data);
        setOriginalAnalystData(data);
        syncOriginalData(data);
      } else {
        setSelectedAnalystData(undefined);
        setOriginalAnalystData(undefined);
      }
    };
    load();
  }, [analystId, isCreatingNew]);

  // Reset state when switching to create-new mode
  useEffect(() => {
    if (isCreatingNew) {
      setSelectedAnalystData(undefined);
      setOriginalAnalystData(undefined);
      resetOriginalData();
      setIsLoadingAnalystDetails(false);
    }
  }, [isCreatingNew]);

  const handleAnalystNameChange = async () => {
    const next = analystNameDraft.trim() || (isCreatingNew ? "New analyst" : "Untitled analyst");

    setSelectedAnalystData(prev => {
      if (!prev) {
        return {
          header: { name: next },
          role: "",
          instructions: "",
          notes: "",
          data_scope: DataScopeProto.create({}),
        } as AnalystProto;
      }
      return { ...prev, header: { ...prev.header, name: next } };
    });
    setIsEditingAnalystName(false);

    const id = selectedAnalystData?.header?.id;
    if (!isCreatingNew && id) {
      if (selectedAnalystData?.header?.name === next) return;
      const updatedHeader = await updateHeader(
        id,
        next,
        selectedAnalystData?.header?.description ?? "",
        MetadataTypeEnum.ANALYST
      );
      if (updatedHeader) {
        setSelectedAnalystData(prev => (prev ? { ...prev, header: updatedHeader } : prev));
        setOriginalAnalystData(prev => (prev ? { ...prev, header: updatedHeader } : prev));
        originalDataRef.current = { ...originalDataRef.current, name: updatedHeader.name || "" };
      }
    }
  };

  useEffect(() => {
    if (!isEditingAnalystName) {
      setAnalystNameDraft(selectedAnalystData?.header?.name || "");
    }
  }, [selectedAnalystData?.header?.id, selectedAnalystData?.header?.name, isEditingAnalystName, isCreatingNew]);

  // Detect changes by comparing current state with original
  const hasChanges = useMemo(() => {
    if (isCreatingNew || !selectedAnalystData || isLoadingAnalystDetails) return false;

    const currentDsUseDefaultScope = selectedAnalystData.data_scope
      ? selectedAnalystData.data_scope.use_default_scope || false
      : true;
    const currentDsMetrics = (selectedAnalystData.data_scope?.metrics || [])
      .map(m => m.header?.id)
      .filter((id): id is string => !!id)
      .sort();
    const currentDsBenchmarks = (selectedAnalystData.data_scope?.benchmarks || [])
      .map(b => b.header?.id)
      .filter((id): id is string => !!id)
      .sort();
    const currentDsColumnScopes = (selectedAnalystData.data_scope?.column_scopes || []).map(cs => ({
      id: cs?.kg_column?.header?.id,
      operator: cs.operator,
      values: cs.values ? [...cs.values].sort() : undefined,
      is_hidden: cs.is_hidden,
      available_values: cs.available_values ? [...cs.available_values].sort() : undefined,
    }));

    const hasChanged =
      (selectedAnalystData.header?.name || "") !== originalDataRef.current.name ||
      (selectedAnalystData.instructions || "") !== originalDataRef.current.agentInstructions ||
      (selectedAnalystData.notes || "") !== originalDataRef.current.notes;

    const dataScopeChanged =
      currentDsUseDefaultScope !== originalDataRef.current.dsUseDefaultScope ||
      !lodash.isEqual(currentDsMetrics, originalDataRef.current.dsMetrics) ||
      !lodash.isEqual(currentDsBenchmarks, originalDataRef.current.dsBenchmarks) ||
      !lodash.isEqual(currentDsColumnScopes, originalDataRef.current.dsColumnScopes);

    return hasChanged || dataScopeChanged;
  }, [selectedAnalystData, isCreatingNew, isLoadingAnalystDetails]);

  const handleSaveAnalyst = async (dataScopeOverride?: DataScopeProto) => {
    const effectiveDataScope = dataScopeOverride ? dataScopeOverride : selectedAnalystData?.data_scope;
    if (!effectiveDataScope || (effectiveDataScope.metrics && effectiveDataScope.metrics.length === 0)) {
      showToast("Please select data scope", "", "warning");
      return;
    }
    if (!selectedAnalystData?.header?.name || selectedAnalystData.header.name.trim() === "") {
      showToast("Please enter analyst name", "", "warning");
      return;
    }

    setIsSaving(true);

    if (!dataScopeOverride && attributesNeedRefresh && refreshAttributeValuesRef.current) {
      try {
        await refreshAttributeValuesRef.current();
      } catch {
        showToast("Failed to refresh attribute values before saving", "", "error");
        setIsSaving(false);
        return;
      }
    }

    if (isCreatingNew) {
      const newAnalyst: AnalystProto = {
        ...selectedAnalystData,
        header: {
          ...selectedAnalystData.header,
          id: uuidv4(),
          type: MetadataTypeEnum.ANALYST,
          created_date_ms: Date.now(),
          last_modified_date_ms: Date.now(),
          last_modified_by: "current_user",
          description: "",
        },
      };
      const result = await createAnalyst(newAnalyst);
      if (result && result.header) {
        showToast("Analyst created successfully", "", "success");
        setAnalystSavedAt(Date.now());
        setSelectedAnalystData(result);
        setOriginalAnalystData(result);
        syncOriginalData(result);
        if (result.header?.id) {
          navigate(`/analyst/${result.header.id}`, { replace: true });
        }
      }
    } else {
      const updatedAnalyst: AnalystProto = {
        ...selectedAnalystData,
        data_scope: effectiveDataScope,
        header: {
          ...selectedAnalystData.header,
          last_modified_date_ms: Date.now(),
          last_modified_by: "current_user",
          description: "",
        },
      };
      const result = await updateAnalyst(updatedAnalyst, true);
      if (result && result.header) {
        showToast("Analyst updated successfully", "", "success");
        setAnalystSavedAt(Date.now());
        setSelectedAnalystData(result);
        setOriginalAnalystData(result);
        syncOriginalData(result);
      }
    }

    setIsSaving(false);
  };

  const handleCancel = () => {
    if (hasChanges && !isCreatingNew && selectedAnalystData) {
      if (originalAnalystData) {
        setSelectedAnalystData({ ...originalAnalystData });
        setHasDiscardedChanges(true);
        showToast("Changes discarded", "", "info");
      } else {
        onBack();
      }
    } else if (isCreatingNew) {
      onBack();
    }
  };

  const handleOpenChat = () => {
    if (analystId) window.open(`/chat?chatAnalystId=${analystId}`, "_blank");
  };

  const handleTabChange = (idx: number) => {
    if (isLoadingAnalystDetails) return;
    if (isCreatingNew || !analystId) return;
    const base = `/analyst/${analystId}`;
    if (idx === 1) navigate(`${base}/memory`);
    else if (idx === 2) navigate(`${base}/jobs`);
    else navigate(base);
  };

  const analystViewTabIndex = isLoadingAnalystDetails ? 0 : getActiveTab;
  const showFooterBar = (isCreatingNew || hasChanges) && !isViewingOrEditingJob;

  return (
    <AnalystDetailViewComponent
      analystId={analystId}
      isCreatingNew={isCreatingNew}
      routeJobId={routeJobId}
      analyst={selectedAnalystData}
      setAnalyst={setSelectedAnalystData}
      isLoadingAnalystDetails={isLoadingAnalystDetails}
      isSaving={isSaving}
      hasChanges={hasChanges}
      isScopeLocked={isScopeLocked}
      analystSavedAt={analystSavedAt}
      isEditingAnalystName={isEditingAnalystName}
      analystNameDraft={analystNameDraft}
      setAnalystNameDraft={setAnalystNameDraft}
      setIsEditingAnalystName={setIsEditingAnalystName}
      onAnalystNameChange={handleAnalystNameChange}
      analystViewTabIndex={analystViewTabIndex}
      onTabChange={handleTabChange}
      showFooterBar={showFooterBar}
      hasDiscardedChanges={hasDiscardedChanges}
      setHasDiscardedChanges={setHasDiscardedChanges}
      originalAnalystData={originalAnalystData}
      onAttributesNeedRefreshChange={setAttributesNeedRefresh}
      onScopeLockedChange={setIsScopeLocked}
      refreshAttributeValuesRef={refreshAttributeValuesRef}
      onTriggerAnalystSave={!isCreatingNew ? handleSaveAnalyst : undefined}
      setIsViewingOrEditingJob={setIsViewingOrEditingJob}
      onSave={() => handleSaveAnalyst()}
      onCancel={handleCancel}
      onOpenChat={handleOpenChat}
      onBack={onBack}
    />
  );
};

export default AnalystDetailView;
